import { memo, useMemo } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuestionnaireManagement } from "src-next/modules/questionnaire-builder/questionnaire-management/hooks";
import { oids } from "@/configs/Settings";

import { useMessageTemplates } from "src-next/modules/crm-clinical/messageTemplates";
import { filterMessageTemplatesByOrganization } from "../../messageTemplates/utils/messageTemplateUiUtils";
import { filterAutomationSelectableTemplates } from "../helpers/automationTemplateUtils";
import type { UseAutomationFormResult } from "../hooks/useAutomationForm";
import AutomationTemplatePicker from "./AutomationTemplatePicker";

export interface AutomationFormProps {
  organizationId: string;
  whatsappBusinessAccountId?: string;
  form: UseAutomationFormResult;
  triggerMoment?: string;
}

const AutomationForm = memo((props: AutomationFormProps) => {
  const {
    organizationId,
    whatsappBusinessAccountId,
    form,
    triggerMoment,
  } = props;

  const { templates, isLoading: isTemplatesLoading } = useMessageTemplates({
    whatsappBusinessAccountId,
    organizationId,
  });
  const { formErrors, setMessageTemplateId } = form;

  const { questionnaires, isLoading: isQuestionnairesLoading } =
    useQuestionnaireManagement({
      organizationId: `${oids.cnpj}-${organizationId}`,
    });

  const selectableTemplates = useMemo(() => {
    const organizationTemplates = filterMessageTemplatesByOrganization(
      templates,
      organizationId,
    );
    return filterAutomationSelectableTemplates(
      organizationTemplates,
      organizationId,
    );
  }, [organizationId, templates]);

  const templatesForPicker = useMemo(() => {
    const selectedId = form.selectedTemplateId?.trim();
    if (!selectedId) return selectableTemplates;

    const alreadyListed = selectableTemplates.some(
      (template) => template.id === selectedId,
    );
    if (alreadyListed) return selectableTemplates;

    const legacySelected = templates.find((template) => template.id === selectedId);
    if (!legacySelected) return selectableTemplates;

    return [legacySelected, ...selectableTemplates];
  }, [form.selectedTemplateId, selectableTemplates, templates]);

  if (isTemplatesLoading || isQuestionnairesLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={form.handleSubmit}
      sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Stack spacing={2}>
        {triggerMoment ? (
          <Typography variant="body2" color="text.secondary">
            <strong>Momento do disparo:</strong> {triggerMoment}
          </Typography>
        ) : null}

        <Typography variant="body2" color="text.secondary">
          Selecione um modelo personalizado da organização. Modelos da aba
          Padrão não aparecem aqui.
        </Typography>

        <AutomationTemplatePicker
          templates={templatesForPicker}
          organizationId={organizationId}
          selectedTemplateId={form.selectedTemplateId}
          onSelectTemplate={setMessageTemplateId}
          questionnaires={questionnaires}
        />

        {formErrors.messageTemplateId ? (
          <Typography variant="caption" color="error.main">
            {formErrors.messageTemplateId.message}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
});

AutomationForm.displayName = "AutomationForm";

export default AutomationForm;
